import './TemplateDetailsBox.scss'
import { TemplateCloneRequest, TemplateScope, TemplateUpdateRequest, TemplateValue } from '../../../models/TemplateLibrary'
import React, { useState } from 'react'
import { Col, Container, Form, Modal as BootstrapModal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { TemplateScopeBadge } from './TemplateScopeBadge'
import { useGetUserByIdQuery } from '../../../store/user/user'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { SystemUserBadge, TemplateCreatedByUserBadge } from './TemplateCreatedByUserBadge'
import FormattedDate from '../../../components/FormattedDate'
import { Button } from '../../../components/Button/Button'
import { TemplateDuplicateModal } from './TemplateDuplicateModal'
import {
  useCloneTemplateMutation,
  useDeleteTemplateMutation,
  useGetTemplateReferencesQuery,
  useListTemplatesQuery,
  useUpdateTemplateMutation,
} from '../../../store/templateLibrary/templateLibrary'
import { useNavigate } from 'react-router-dom'
import { Modal } from '../../../components/Modal/Modal'
import { SelectDropdown } from '../../../components/SelectDropdown/SelectDropdown'
import { TemplateScopeMapping } from './TemplateScopeMapping'
import { TemplateEditModal } from './TemplateEditModal'
import { ZOCKS_SYSTEM_USER_ID } from './utils'
import { useAccount } from '../../../common/useAccount'
import { SuccessToast } from '../../../components/SuccessToast/SuccessToast'

export interface TemplateDetailsBoxProps {
  templateValue: TemplateValue
  readOnly: boolean
  showControls?: boolean
}

export const TemplateDetailsBox: React.FC<TemplateDetailsBoxProps> = ({ templateValue, readOnly, showControls = true }) => {
  const navigate = useNavigate()
  const { accountId, account } = useAccount()
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateTemplateMutation()
  const [cloneTemplate, { isLoading: isCloning }] = useCloneTemplateMutation()
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteTemplateMutation()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDownloadToast, setShowDownloadToast] = useState<boolean>(false)

  const templatesLocked = account?.enterpriseConfig.lockTemplates ?? false

  const onDuplicateClick = () => {
    setShowDuplicateModal(true)
  }

  const handleEdit = async (request: TemplateUpdateRequest) => {
    await updateTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    setShowEditModal(false)
  }

  const handleCancelEdit = () => {
    setShowEditModal(false)
  }

  const handleDuplicate = async (request: TemplateCloneRequest) => {
    const clone = await cloneTemplate({ accountId, templateId: templateValue.id, request }).unwrap()
    setShowDuplicateModal(false)
    navigate(`/workspace/setup/templates/template/${clone.id}`, { replace: true })
  }

  const handleCancelDuplication = () => {
    setShowDuplicateModal(false)
  }

  const onDeleteClick = () => {
    setShowDeleteModal(true)
  }

  const handleDelete = async (replaceWithTemplateId?: string | null) => {
    await deleteTemplate({ accountId, templateId: templateValue.id, replaceWithTemplateId }).unwrap()
    setShowDeleteModal(false)
    navigate('/workspace/setup/templates')
  }

  const handleCancelDeletion = () => {
    setShowDeleteModal(false)
  }

  const handleTemplateDetailsClicked = () => {
    if (readOnly) {
      return
    }
    setShowEditModal(true)
  }

  const handleExportTemplate = async () => {
    const formJson = JSON.stringify(
      {
        type: templateValue.type,
        typeId: templateValue.typeId,
        name: templateValue.name,
        description: templateValue.description,
        template: templateValue.template.template,
      },
      null,
      2,
    )

    const blob = new Blob([formJson], { type: 'application/json' })

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const safeTemplateName = (() => {
      if (!templateValue.name) {
        return 'template'
      }
      const sanitized = templateValue.name
        .replace(/[^a-z0-9]/gi, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
      return sanitized || 'template'
    })()
    link.href = url
    link.download = `${safeTemplateName}_template.json`
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    setShowDownloadToast(true)
  }

  return (
    <>
      {showEditModal ? (
        <TemplateEditModal show={showEditModal} loading={isUpdating} templateValue={templateValue} onSave={handleEdit} onCancel={handleCancelEdit} />
      ) : null}
      {showDuplicateModal ? (
        <TemplateDuplicateModal
          show={showDuplicateModal}
          loading={isCloning}
          templateValue={templateValue}
          onDuplicate={handleDuplicate}
          onCancel={handleCancelDuplication}
        />
      ) : null}
      {showDeleteModal ? (
        <TemplateDeleteConfirmationModal
          show={showDeleteModal}
          loading={isDeleting}
          onCancel={handleCancelDeletion}
          onDelete={handleDelete}
          templateValue={templateValue}
        />
      ) : null}
      <SuccessToast
        position={'bottom-center'}
        message={`Template "${templateValue.name}" downloaded.`}
        show={showDownloadToast}
        onClose={() => setShowDownloadToast(false)}
      />
      <Container className='zocks-bg-color-white p-3 border rounded-3'>
        <OverlayTrigger placement='top' overlay={<Tooltip>Edit template details</Tooltip>} show={readOnly ? false : undefined}>
          <div className={`template-name-description-container ${!readOnly ? 'enabled' : ''}`} onClick={handleTemplateDetailsClicked}>
            <Row className='pb-3'>
              <Col>
                <h5>{templateValue.name}</h5>
              </Col>
            </Row>
            <Row>
              <Col>
                <div className='paragraph-medium zocks-text-gray-600'>{templateValue.description}</div>
              </Col>
            </Row>
          </div>
        </OverlayTrigger>
        <Row className='pt-4'>
          <Col>
            <TemplateDetailsLabel label='Type' />
          </Col>
          <Col className='d-flex justify-content-start'>
            <TemplateTypeBadge templateValue={templateValue} />
          </Col>
        </Row>
        <Row className='pt-3'>
          <Col>
            <TemplateDetailsLabel label='Access' />
          </Col>
          <Col className='d-flex justify-content-start paragraph-small'>
            <TemplateScopeBadge scope={templateValue.scope} />
          </Col>
        </Row>
        <Row className='pt-3'>
          <Col>
            <TemplateDetailsLabel label='Created by' />
          </Col>
          <Col className='d-flex justify-content-start paragraph-small'>
            {'createdBy' in templateValue && templateValue.createdBy !== ZOCKS_SYSTEM_USER_ID ? (
              <TemplateCreatedBy userId={templateValue.createdBy} />
            ) : templateValue.scope === 'system' || ('createdBy' in templateValue && templateValue.createdBy === ZOCKS_SYSTEM_USER_ID) ? (
              <SystemUserBadge />
            ) : null}
          </Col>
        </Row>
        {'updatedAt' in templateValue ? (
          <Row className='pt-3'>
            <Col>
              <TemplateDetailsLabel label='Updated' />
            </Col>
            <Col className='d-flex justify-content-start'>
              <div className='paragraph-small zocks-text-gray-900'>
                <FormattedDate date={templateValue.updatedAt} />
              </div>
            </Col>
          </Row>
        ) : null}
        {showControls ? (
          <Row className='pt-4'>
            <Col className='d-flex justify-content-start gap-2'>
              {!templatesLocked ? (
                <Button variant='grayGhost' className='button-large' onClick={onDuplicateClick} title='Duplicate'>
                  <i className='bi bi-copy' /> Duplicate
                </Button>
              ) : null}
              <OverlayTrigger placement='bottom' overlay={<Tooltip id='button-tooltip-2'>Export template to file</Tooltip>}>
                <Button variant='grayGhost' className='button-large' onClick={handleExportTemplate} aria-label='Export template to file'>
                  <i className='bi bi-download' />
                </Button>
              </OverlayTrigger>
              {!readOnly ? (
                <Button variant='grayGhost' className='button-large px-2' onClick={onDeleteClick} title='Delete' loading={isDeleting}>
                  <i className='bi bi-trash' />
                </Button>
              ) : null}
            </Col>
          </Row>
        ) : null}
      </Container>
    </>
  )
}

interface TemplateDetailsLabelProps {
  label: string
}

const TemplateDetailsLabel: React.FC<TemplateDetailsLabelProps> = ({ label }) => {
  return <div className='paragraph-small fw-bold zocks-text-gray-600'>{label}</div>
}

interface TemplateCreatedByProps {
  userId: string
}

const TemplateCreatedBy: React.FC<TemplateCreatedByProps> = ({ userId }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: user } = useGetUserByIdQuery({ accountId, userId })
  if (!user) {
    return <div>{userId}</div>
  }

  return <TemplateCreatedByUserBadge createdByUser={user.data} />
}

interface TemplateDeleteConfirmationModalProps {
  show: boolean
  loading: boolean
  onCancel: () => void
  onDelete: (replaceWithTemplateId?: string | null) => void
  templateValue: TemplateValue
}

export const TemplateDeleteConfirmationModal: React.FC<TemplateDeleteConfirmationModalProps> = ({ show, loading, onCancel, onDelete, templateValue }) => {
  const accountId = useAppSelector(selectAccountId)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const { data: templateReferences } = useGetTemplateReferencesQuery({ accountId, templateId: templateValue.id })

  const body = !templateReferences ? (
    <Spinner />
  ) : templateReferences.count === 0 ? (
    <HasNoReferenceTemplateDeleteModalBody templateValue={templateValue} />
  ) : (
    <HasReferenceTemplateDeleteModalBody templateValue={templateValue} referenceCount={templateReferences.count} onSelectTemplateId={setSelectedTemplateId} />
  )
  return (
    <Modal
      className='modal-lg'
      header={<BootstrapModal.Title>Delete template</BootstrapModal.Title>}
      body={body}
      show={show}
      onHide={onCancel}
      footerClassName='pt-0'
      footerPrimaryActionTitle='Delete template'
      footerPrimaryActionButtonVariant='danger'
      footerPrimaryActionLoading={loading}
      footerPrimaryActionDisabled={loading || !templateReferences || (templateReferences.count != 0 && !selectedTemplateId)}
      showFooterActions={true}
      centered
      onFooterPrimaryAction={() => onDelete(selectedTemplateId)}
      onFooterSecondaryAction={onCancel}
      headerBodySeparator={true}
    />
  )
}

interface TemplateDeleteModalBodyProps {
  templateValue: TemplateValue
}

const HasNoReferenceTemplateDeleteModalBody: React.FC<TemplateDeleteModalBodyProps> = ({ templateValue }) => {
  return (
    <Container>
      <Row>
        <div>The {templateValue.name} template is not used in any meeting types. It can be safely deleted. This action can't be undone.</div>
      </Row>
    </Container>
  )
}

interface HasReferenceTemplateDeleteModalBodyProps extends TemplateDeleteModalBodyProps {
  referenceCount: number
  onSelectTemplateId: (templateId: string) => void
}

const HasReferenceTemplateDeleteModalBody: React.FC<HasReferenceTemplateDeleteModalBodyProps> = ({ templateValue, referenceCount, onSelectTemplateId }) => {
  const accountId = useAppSelector(selectAccountId)
  const scopeFilter: TemplateScope[] =
    templateValue.scope == 'system' || templateValue.scope == 'account' ? ['account', 'system'] : ['account', 'system', 'user']
  const { data: templates } = useListTemplatesQuery({
    accountId,
    scopes: scopeFilter,
    type: templateValue.type,
    typeId: templateValue.typeId,
    createdBy: 'createdBy' in templateValue ? templateValue.createdBy : undefined,
  })

  return (
    <Container>
      <Row>
        <p>This template is used in {referenceCount} meeting types. Choose a replacement before deleting.</p>
      </Row>
      <Row>
        <Form.Label>Template to delete</Form.Label>
        <p>{templateValue.name}</p>
      </Row>
      <Row>
        <Form.Label>Replace with</Form.Label>
        <Col>
          {templates?.data ? (
            <SelectDropdown
              initialSelectedOption={null}
              options={templates.data
                .filter((template) => template.id !== templateValue.id)
                .map((template) => ({
                  id: template.id,
                  label: `${template.name} (${TemplateScopeMapping[template.scope].title})`,
                  displayLabel: (
                    <div className='zocks-py-0.125 inline-block w-auto' key={template.id}>
                      <span>{template.name}</span>
                      <span className='zocks-text-gray-600'> ({TemplateScopeMapping[template.scope].title})</span>
                    </div>
                  ),
                }))}
              onOptionSelected={(option) => onSelectTemplateId(option.id)}
              placeholder='Select template'
            />
          ) : (
            <Spinner />
          )}
        </Col>
      </Row>
    </Container>
  )
}
